import { Capacitor } from "@capacitor/core";
import { get } from "svelte/store";
import { dialogData } from "../stores/dialogDataStore";
import { initialSharedAccountState, sharedAccountState } from "../stores/sharedAccountStore";
import { Roles, dialogTypes, serverlessRoutes, version } from "./constants";
import { clearSharedTrainerChatState } from "./sharedTrainerChat";
import { storage } from "./storage";

type SharedAccountUser = {
  id?: number;
  roleId?: number;
  name?: string;
};

type SharedAccountContext = {
  accountId: number;
  name: string | null;
};

type SharedUnlockResponse = {
  sharedSessionToken?: string;
  sessionToken?: string;
  token?: string;
  expiresAt?: string;
  sharedAccountUser?: {
    id?: number;
    label?: string;
    name?: string;
  };
};

const sharedAccountContextStorageKey = "sharedAccountContext";
const sharedAccountSessionTokenStorageKey = "sharedAccountSessionToken";

let pendingUnlockPromise: Promise<boolean> | null = null;
let resolvePendingUnlock: ((value: boolean) => void) | null = null;

const resetState = (): void => {
  sharedAccountState.set({ ...initialSharedAccountState });
};

const createUnlockPromise = (): Promise<boolean> => {
  if (pendingUnlockPromise) {
    return pendingUnlockPromise;
  }

  pendingUnlockPromise = new Promise<boolean>((resolve) => {
    resolvePendingUnlock = resolve;
  });

  return pendingUnlockPromise;
};

const resolveUnlockPromise = (value: boolean): void => {
  resolvePendingUnlock?.(value);
  pendingUnlockPromise = null;
  resolvePendingUnlock = null;
};

const getAppInfoHeader = (): string =>
  JSON.stringify({
    version,
    platform: Capacitor.isNativePlatform() ? Capacitor.getPlatform() : "web",
  });

const normalizeResponsePayload = (response: any): any => {
  if (
    response?.data &&
    typeof response.data === "object" &&
    (
      response.data.accessToken ||
      response.data.sharedAccount ||
      response.data.user ||
      response.data.sharedSessionToken ||
      response.data.sessionToken ||
      response.data.token
    )
  ) {
    return response.data;
  }

  return response;
};

const closeSharedAccountDialog = (): void => {
  dialogData.update((current) => {
    if (current.type !== dialogTypes.SHARED_ACCOUNT_PASSWORD) {
      return current;
    }

    return {
      ...current,
      type: "",
      data: {},
      isCloseable: true,
      component: undefined,
    };
  });
};

const openSharedAccountDialog = (context: SharedAccountContext): void => {
  dialogData.update((current) => ({
    ...current,
    type: dialogTypes.SHARED_ACCOUNT_PASSWORD,
    data: {
      accountId: context.accountId,
      name: context.name,
    },
    isCloseable: false,
    component: undefined,
  }));
};

const readStoredContext = async (): Promise<SharedAccountContext | null> => {
  const context = await storage.get<SharedAccountContext>(sharedAccountContextStorageKey);

  if (!context?.accountId) {
    return null;
  }

  return context;
};

const writeStoredContext = async (context: SharedAccountContext | null): Promise<void> => {
  if (!context) {
    await storage.remove(sharedAccountContextStorageKey);
    return;
  }

  await storage.set(sharedAccountContextStorageKey, context);
};

const readStoredSessionToken = async (): Promise<string | null> => {
  const sessionToken = await storage.get<string>(sharedAccountSessionTokenStorageKey);
  return sessionToken || null;
};

const writeStoredSessionToken = async (sessionToken: string | null): Promise<void> => {
  if (!sessionToken) {
    await storage.remove(sharedAccountSessionTokenStorageKey);
    return;
  }

  await storage.set(sharedAccountSessionTokenStorageKey, sessionToken);
};

const hydrateStore = async (): Promise<void> => {
  const [context, sessionToken] = await Promise.all([
    readStoredContext(),
    readStoredSessionToken(),
  ]);

  if (!context) {
    resetState();
    return;
  }

  sharedAccountState.set({
    accountId: context.accountId,
    name: context.name,
    sessionToken,
    isRequired: true,
    isUnlocked: Boolean(sessionToken),
    isPromptOpen: false,
    unlockedAt: sessionToken ? new Date().toISOString() : null,
  });
};

const extractSharedAccountContext = (response: any): SharedAccountContext | null => {
  const payload = normalizeResponsePayload(response);
  const sharedAccount = payload?.sharedAccount;

  const isRequired = Boolean(
    sharedAccount?.required ??
      payload?.sharedAccountRequired ??
      payload?.requiresSharedAccountUnlock ??
      payload?.user?.requiresSharedAccountUnlock
  );

  if (!isRequired) {
    return null;
  }

  const accountId = Number(
    sharedAccount?.accountId ??
      sharedAccount?.id ??
      payload?.user?.id
  );

  if (!accountId || Number.isNaN(accountId)) {
    throw new Error("Missing shared account metadata in auth response.");
  }

  return {
    accountId,
    name:
      sharedAccount?.accountName ??
      sharedAccount?.name ??
      payload?.user?.name ??
      null,
  };
};

const parseUnlockResponse = (response: any): SharedUnlockResponse => normalizeResponsePayload(response);

export const isSharedAccountTarget = (user?: SharedAccountUser | null): boolean => {
  if (!user?.id || user.roleId !== Roles.Trainer) {
    return false;
  }

  return [13, 87649].includes(user.id);
};

export const prepareSharedAccountContextFromAuthResponse = async (response: any): Promise<void> => {
  const context = extractSharedAccountContext(response);

  if (!context) {
    await clearSharedAccountAccess();
    return;
  }

  await writeStoredContext(context);
  await writeStoredSessionToken(null);

  sharedAccountState.set({
    accountId: context.accountId,
    name: context.name,
    sessionToken: null,
    isRequired: true,
    isUnlocked: false,
    isPromptOpen: false,
    unlockedAt: null,
  });
};

export const reconcileSharedAccountContextWithUser = async (
  user?: SharedAccountUser | null
): Promise<void> => {
  const storedContext = await readStoredContext();

  if (!storedContext) {
    return;
  }

  if (!isSharedAccountTarget(user) || user?.id !== storedContext.accountId) {
    await clearSharedAccountAccess();
    return;
  }

  const sessionToken = await readStoredSessionToken();
  sharedAccountState.set({
    accountId: storedContext.accountId,
    name: user?.name ?? storedContext.name,
    sessionToken,
    isRequired: true,
    isUnlocked: Boolean(sessionToken),
    isPromptOpen: false,
    unlockedAt: sessionToken ? new Date().toISOString() : null,
  });
};

export const ensureSharedAccountAccess = async (): Promise<boolean> => {
  const storedContext = await readStoredContext();

  if (!storedContext) {
    resetState();
    return true;
  }

  const sessionToken = await readStoredSessionToken();
  if (sessionToken) {
    sharedAccountState.set({
      accountId: storedContext.accountId,
      name: storedContext.name,
      sessionToken,
      isRequired: true,
      isUnlocked: true,
      isPromptOpen: false,
      unlockedAt: new Date().toISOString(),
    });
    return true;
  }

  sharedAccountState.set({
    accountId: storedContext.accountId,
    name: storedContext.name,
    sessionToken: null,
    isRequired: true,
    isUnlocked: false,
    isPromptOpen: true,
    unlockedAt: null,
  });

  openSharedAccountDialog(storedContext);
  return createUnlockPromise();
};

export const completeSharedAccountAccess = async (password: string): Promise<void> => {
  const normalizedPassword = password.trim();

  if (!normalizedPassword) {
    throw new Error("Personal password is required.");
  }

  const context = await readStoredContext();
  if (!context?.accountId) {
    throw new Error("Missing shared account context.");
  }

  const jwt = await storage.get<string>("authJwt");
  if (!jwt) {
    throw new Error("Authentication expired. Please sign in again.");
  }

  const response = await fetch(`${serverlessRoutes.SHARED_ACCOUNT}/unlock`, {
    method: "POST",
    mode: "cors",
    cache: "no-cache",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${jwt}`,
      "X-App-Version": getAppInfoHeader(),
    },
    redirect: "follow",
    referrerPolicy: "no-referrer",
    body: JSON.stringify({
      accountId: context.accountId,
      personalPassword: normalizedPassword,
    }),
  });

  let responsePayload: SharedUnlockResponse | { message?: string; error?: string } = {};
  try {
    responsePayload = await response.json();
  } catch (error) {
    responsePayload = {};
  }

  if (!response.ok) {
    const errorMessage =
      (responsePayload as { message?: string; error?: string }).message ||
      (responsePayload as { message?: string; error?: string }).error ||
      "Personal password verification failed.";
    throw new Error(errorMessage);
  }

  const parsedResponse = parseUnlockResponse(responsePayload);
  const sessionToken =
    parsedResponse.sharedSessionToken || parsedResponse.sessionToken || parsedResponse.token;

  if (!sessionToken) {
    throw new Error("Missing shared session token in unlock response.");
  }

  const unlockedAt = new Date().toISOString();

  await writeStoredSessionToken(sessionToken);

  sharedAccountState.set({
    accountId: context.accountId,
    name: parsedResponse.sharedAccountUser?.label || parsedResponse.sharedAccountUser?.name || context.name,
    sessionToken,
    isRequired: true,
    isUnlocked: true,
    isPromptOpen: false,
    unlockedAt,
  });

  closeSharedAccountDialog();
  resolveUnlockPromise(true);
};

export const waitForSharedAccountAccess = async (): Promise<void> => {
  const currentState = get(sharedAccountState);

  if (currentState.isRequired && !currentState.isUnlocked && !pendingUnlockPromise) {
    await ensureSharedAccountAccess();
  }

  if (pendingUnlockPromise) {
    await pendingUnlockPromise;
  }
};

export const clearSharedAccountAccess = async (): Promise<void> => {
  closeSharedAccountDialog();
  await Promise.all([
    writeStoredContext(null),
    writeStoredSessionToken(null),
  ]);
  clearSharedTrainerChatState();
  resetState();
  resolveUnlockPromise(false);
};

export const getSharedAccountHeaders = async (): Promise<Record<string, string>> => {
  const sessionToken = await readStoredSessionToken();

  if (!sessionToken) {
    return {};
  }

  return {
    "X-Shared-Session-Token": sessionToken,
  };
};

export const getSharedAccountSocketQuery = async (): Promise<Record<string, string>> => {
  const sessionToken = await readStoredSessionToken();

  if (!sessionToken) {
    return {};
  }

  return {
    sharedSessionToken: sessionToken,
  };
};

export const hydrateSharedAccountState = async (): Promise<void> => {
  await hydrateStore();
};
